import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const PLATFORMS = { android: "Android — Google Play", ios: "iOS — App Store" };

export default function StoreLinks() {
    const [links, setLinks] = useState(null);
    const [forms, setForms] = useState({});

    const load = () =>
        api.get("/admin/store-links").then((r) => {
            setLinks(r.data);
            const f = {};
            r.data.forEach((l) => (f[l.platform] = { url: l.url || "", enabled: !!l.enabled, label: l.label || "", badge: l.badge || "", unavailable_message: l.unavailable_message || "", note: l.note || "" }));
            setForms(f);
        }).catch((e) => toast.error(apiError(e)));
    useEffect(() => { load(); }, []);

    const save = async (platform) => {
        try {
            await api.put(`/admin/store-links/${platform}`, forms[platform]);
            toast.success("Lien enregistré — effectif immédiatement sur le site.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    if (!links) return <p className="text-pineneedle">Chargement…</p>;

    return (
        <div data-testid="admin-store-links">
            <PageHeader title="Liens de téléchargement" subtitle="Les boutons publics utilisent exclusivement ces URLs. Un lien absent ou désactivé n'apparaît jamais comme un téléchargement disponible." />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {links.map((l) => {
                    const f = forms[l.platform] || {};
                    const set = (k, v) => setForms({ ...forms, [l.platform]: { ...f, [k]: v } });
                    return (
                        <div key={l.platform} className="rounded-2xl bg-white border border-gold/25 p-6 shadow-sm space-y-4" data-testid={`store-link-card-${l.platform}`}>
                            <div className="flex items-center justify-between">
                                <h3 className="font-heading font-bold text-forest">{PLATFORMS[l.platform]}</h3>
                                <span className="text-xs text-pineneedle" data-testid={`clicks-${l.platform}`}>{l.clicks} clic(s) enregistré(s)</span>
                            </div>
                            <div>
                                <Label>URL officielle (HTTPS, {l.platform === "android" ? "play.google.com" : "apps.apple.com"})</Label>
                                <Input value={f.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="https://…" data-testid={`url-input-${l.platform}`} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><Label>Libellé du bouton</Label><Input value={f.label || ""} onChange={(e) => set("label", e.target.value)} data-testid={`label-input-${l.platform}`} /></div>
                                <div><Label>Badge (ex : version de test)</Label><Input value={f.badge || ""} onChange={(e) => set("badge", e.target.value)} data-testid={`badge-input-${l.platform}`} /></div>
                            </div>
                            <div><Label>Message si indisponible</Label><Input value={f.unavailable_message || ""} onChange={(e) => set("unavailable_message", e.target.value)} data-testid={`unavailable-input-${l.platform}`} /></div>
                            <div><Label>Note interne</Label><Input value={f.note || ""} onChange={(e) => set("note", e.target.value)} data-testid={`note-input-${l.platform}`} /></div>
                            <label className="flex items-center gap-3 text-sm text-pineneedle">
                                <Switch checked={!!f.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid={`enabled-switch-${l.platform}`} /> Plateforme activée (bouton visible et cliquable)
                            </label>
                            {l.url?.includes("internaltest") && (
                                <p className="text-xs bg-amber-100 text-amber-900 rounded-lg px-3 py-2">
                                    Lien de test interne détecté : il n'est accessible qu'aux testeurs enregistrés sur Google Play. Remplacez-le par le lien public officiel lors de la publication.
                                </p>
                            )}
                            <div className="flex items-center gap-3">
                                <Button onClick={() => save(l.platform)} className="bg-forest text-cream" data-testid={`save-link-${l.platform}`}>Enregistrer</Button>
                                {l.url && (
                                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-xs text-gold flex items-center gap-1 hover:underline" data-testid={`verify-link-${l.platform}`}>
                                        Vérifier la destination <ExternalLink size={12} />
                                    </a>
                                )}
                            </div>
                            <p className="text-[11px] text-pineneedle/60">Dernière modification : {fmtDate(l.updated_at)} par {l.updated_by}</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
